import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Award, Wrench, PartyPopper, CheckCircle, ArrowRight, AlertCircle } from 'lucide-react';

type ChampionsSectionProps = {
  cityName?: string;
  applicationUrl?: string;
};

export function ChampionsSection({
  cityName = 'your community',
  applicationUrl = '/apply-champion',
}: ChampionsSectionProps) {
  const navigate = useNavigate();

  const navigateToApplication = () => {
    navigate(applicationUrl);
  };

  const steps = [
    {
      icon: Award,
      title: 'Find Community Champions',
      description: `We need 5-10 ${cityName} residents to help shape this platform AND potentially become the first leaders of the ${cityName} Digital Community governance structure (Community Elect, Community Directors, Department leads). Champions will test features, gather feedback, and lead the launch. You'll also learn the transparent governance model that puts residents in control.`,
    },
    {
      icon: Wrench,
      title: 'Build It Together',
      description: `Champions work with us to prioritize features, customize the platform for ${cityName}, and make sure it actually solves real problems. This isn't top-down. It's a collaboration.`,
    },
    {
      icon: PartyPopper,
      title: 'Free Trial for Everyone',
      description: `Once the platform is ready, every ${cityName} resident gets 1 month free to test it. No credit card required. Just sign up, explore, and decide if it's right for your community.`,
    },
    {
      icon: CheckCircle,
      title: 'Community Decides',
      description: `If the community loves it, we launch. Residents who want to keep using the platform subscribe for a small monthly fee ($2.5) to keep it independent and ad-free. If ${cityName} doesn't want it, then keep doing what you're doing. No Worries.`,
    },
  ];

  return (
    <>
      {/* How It Works Section */}
      <section id="how-it-works" className="py-12 sm:py-16 md:py-20 bg-background">
        <div className="container px-4 sm:px-6">
          <div className="max-w-4xl mx-auto text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4 px-2">
              We Partner with You.
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground px-4 leading-relaxed">
              This platform isn't ready to launch in {cityName} yet, and that's by design. We
              believe in community-led solutions, which means we need your input first.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 max-w-5xl mx-auto">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <Card
                  key={index}
                  className={`card-hover-elegant group feature-card-stagger-${index + 1}`}
                >
                  <CardContent className="p-6 sm:p-8">
                    <div className="flex items-start gap-3 sm:gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 group-hover:scale-110 transition-all duration-300">
                          <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                        </div>
                      </div>
                      <div>
                        <h3 className="text-lg sm:text-xl font-bold mb-2 group-hover:text-primary transition-colors">
                          {step.title}
                        </h3>
                        <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Champions CTA Section */}
      <section id="champions" className="py-12 sm:py-16 md:py-20 bg-primary/5">
        <div className="container px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <Card className="border-2 border-primary/20 hover:border-primary/30 transition-all duration-300 shadow-lg hover:shadow-xl overflow-hidden">
              <div className="relative aspect-video w-full">
                <img
                  src="/media_shared/champion-hero.png"
                  alt={`Diverse group of ${cityName} community champions`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  width={1200}
                  height={675}
                />
              </div>
              <CardContent className="p-6 sm:p-8 md:p-12">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6 text-center px-2">
                  Want to Lead the Way?
                </h2>
                <div className="space-y-3 sm:space-y-4 text-base sm:text-lg text-muted-foreground leading-relaxed mb-6 sm:mb-8">
                  <p>
                    {cityName} should be able to organize itself to lead by example, not wait on
                    politicians. We're building a {cityName} Community App for residents to organize
                    with secure voting, digital treasuries for pooling funds, group buying power to
                    negotiate in-town discounts, facility booking, dispute resolution tools, and
                    crowdfunding for community projects. We need 7-20 Champions to shape it. Ready
                    to step up?
                  </p>
                  <div className="bg-background rounded-lg p-4 sm:p-6 border-l-4 border-primary">
                    <p className="font-semibold text-foreground mb-2 sm:mb-3 text-sm sm:text-base">
                      Community Champions will work directly with us to:
                    </p>
                    <ul className="space-y-1.5 sm:space-y-2 list-disc list-inside text-muted-foreground text-sm sm:text-base">
                      <li>Tell us what the {cityName} Community App needs</li>
                      <li>Test and shape the platform for {cityName}</li>
                      <li>Gather feedback from neighbors</li>
                      <li>Lead the free trial launch</li>
                      <li>Help build a movement for civic empowerment</li>
                    </ul>
                  </div>

                  {/* Champion Benefits Callout */}
                  <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg p-4 sm:p-6 border-2 border-amber-300 shadow-sm">
                    <div className="flex items-start gap-3">
                      <Award className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-bold text-amber-900 mb-2 text-sm sm:text-base">
                          Champion Rewards:
                        </p>
                        <ul className="space-y-1 text-amber-800 text-sm">
                          <li>
                            ✨ <strong>Lifetime Pro Access</strong> (no payment, ever)
                          </li>
                          <li>
                            👑 <strong>Founding Member Badge</strong> in the app
                          </li>
                          <li>
                            🚀 <strong>Early access</strong> to new features
                          </li>
                          <li>
                            🤝 <strong>Leadership role</strong> in community governance
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <p>
                    In return, you'll get early access, a leadership role, and{' '}
                    <strong className="text-primary">lifetime Pro access (free forever)</strong>.
                    But most importantly, you'll help build something that empowers your community
                    that lasts forever.
                  </p>
                </div>

                {/* Selection Disclaimer */}
                <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4 sm:p-5 text-left">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="space-y-2">
                      <p className="font-semibold text-amber-900 text-sm sm:text-base">
                        Before You Apply
                      </p>
                      <p className="text-xs sm:text-sm text-amber-800 leading-relaxed">
                        <strong>Application does not guarantee selection.</strong> We carefully
                        review all submissions and select Champions based on community involvement,
                        reach potential, and overall fit. Selection may include follow-up
                        conversations to assess compatibility.
                      </p>
                      <p className="text-xs sm:text-sm text-amber-800 leading-relaxed">
                        <strong>Launch is conditional:</strong> Even if selected, platform launch
                        and lifetime benefits depend on sufficient Champion participation and
                        demonstrated community interest. We only move forward if {cityName} shows it
                        genuinely wants its own Community App.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="text-center pt-5">
                  <Button
                    size="lg"
                    onClick={navigateToApplication}
                    className="w-full sm:w-auto text-sm sm:text-base px-6 sm:px-8 py-5 sm:py-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 group"
                  >
                    I Want to Be a Champion
                    <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </>
  );
}
